import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { NavLink } from 'react-router-dom';
import { Badge, Link as LinkToHome } from '@mui/material';
import { withStyles } from '@mui/styles';
import { withKeycloak } from '@react-keycloak/web';
import Button from '@mui/material/Button';
import classNames from 'classnames';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuIcon from '@mui/icons-material/Menu';
import FilterListIcon from '@mui/icons-material/FilterList';
import MenuItem from '@mui/material/MenuItem';
import MoreIcon from '@mui/icons-material/MoreVert';
import Toolbar from '@mui/material/Toolbar';
import { withTranslation } from 'react-i18next';
import {
    badgesFilter,
    DEVICE_FILTER_FIELD_INIT,
    isEnoughPermissions,
    LICENSE_FILTER_FIELD_INIT, MOBILE_DEVICE_FILTER_FIELD_INIT, VM_FILTER_FIELD_INIT
} from '../../utils';

import logo from '../../assets/images/logo.svg';
import LanguageSwitcher from './LanguageSwitcher';
import { logOut } from '../../actions';

export const drawerIconTypes = {
    MenuIcon: <MenuIcon />,
    FilterIcon: <FilterListIcon />,
};

const styles = theme => ({
    root: {
        flexGrow: 1,
        fontFamily: '\'DM Sans\', Roboto, \'Fira Sans\', sans-serif'
    },
    grow: {
        flexGrow: 1
    },
    menuGroup: {
        flexGrow: 1,
        flexWrap: 'wrap',
        marginLeft: 16,
        display: 'flex',
        minHeight: 64
    },
    logoWrapper: {
        flexGrow: 0,
        flexShrink: 1,
        flexBasis: 300,
        marginLeft: '25px'
    },
    logo: {
        margin: '0 10px 0 0',
        fontFamily: '\'DM Sans\', sans-serif',
        fontSize: '18.5px',
        color: 'white',
    },
    button: {
        fontSize: '0.9rem',
        textTransform: 'none',
        borderRadius: 0,
        transition: 'none',
        borderBottom: '3px solid transparent',
        fontWeight: 400,
        paddingTop: 12,
        paddingLeft: 10,
        paddingRight: 10,
        '&.active': {
            borderBottom: `3px solid ${theme.palette.secondary.main}`,
            fontWeight: 'bold',
            color: theme.palette.secondary.main
        }
    },
    menuButton: {
        marginLeft: -8
    },
    hide: {
        display: 'none'
    },
    sectionDesktop: {
        display: 'none',
        [theme.breakpoints.up(1420)]: {
            display: 'flex'
        }
    },
    sectionMobile: {
        display: 'flex',
        [theme.breakpoints.up(1420)]: {
            display: 'none'
        }
    },
    mobileMenuItem: {
        '&.active': {
            backgroundColor: theme.palette.action.selected
        }
    },
    badge: {
        '& .MuiBadge-badge': {
            backgroundColor: theme.palette.secondary.main,
            color: theme.palette.primary.main
        }
    }
});

const AdapterNavLink = React.forwardRef((props, ref) => (
    <NavLink
        {...props}
        ref={ref}
    />
));

const calculateTotalFilters = (filters, initialFilters) => {
    if (filters && initialFilters) {
        return Object.entries(filters).reduce((acc, arr) => {
            if (Array.isArray(initialFilters)) {
                initialFilters.forEach(el => badgesFilter(el, arr, acc, el));
                return acc;
            } else {
                Object.keys(initialFilters).forEach(el => badgesFilter(initialFilters[el], arr, acc, el));
                return acc;
            }
        }, { total: 0 });
    }

    return { total: 0 };

};

class Header extends PureComponent {
    state = {
        mobileMoreAnchorEl: null,
    };

    menuItems = [
        { translateId: 'myAssets', routeTo: '/my-assets' },
        { translateId: 'employeeAssets', routeTo: '/employee-assets' },
        { translateId: 'devices', routeTo: '/device' },
        { translateId: 'mobileDevices', routeTo: '/mobile-device' },
        { translateId: 'licenses', routeTo: '/license' },
        { translateId: 'virtualMachines', routeTo: '/virtual-machine' },
        { translateId: 'usersWithoutComputers', routeTo: '/users-without-computers' },
        { translateId: 'reports', routeTo: '/reports' },
        { translateId: 'administration', routeTo: '/admin' }
    ];

    componentDidUpdate(prevProps) {
        if (!this.props.keycloak.authenticated && prevProps.keycloak.authenticated) {
            this.props.onDrawerClose();
        }
    }

    handleMobileMenuOpen = event => {
        this.setState({ mobileMoreAnchorEl: event.currentTarget });
    };

    handleMenuClose = () => {
        this.handleMobileMenuClose();
    };

    handleMobileMenuClose = () => {
        this.setState({ mobileMoreAnchorEl: null });
    };

    getFiltersDataForPage = url => {
        switch (url) {
            case '/device':
                return {
                    filters: this.props.devicesFilters,
                    initFilters: DEVICE_FILTER_FIELD_INIT
                };
            case '/mobile-device':
                return {
                    filters: this.props.mobileDevicesFilters,
                    initFilters: MOBILE_DEVICE_FILTER_FIELD_INIT
                };
            case '/license':
                return {
                    filters: this.props.licensesFilters,
                    initFilters: LICENSE_FILTER_FIELD_INIT
                };
            case '/virtual-machine':
                return {
                    filters: this.props.VMFilters,
                    initFilters: VM_FILTER_FIELD_INIT
                };
            default:
                return {
                    filters: null,
                    initFilters: null
                };
        }
    }

    handleLogout = () => {
        const { keycloak, logOut, history } = this.props;
        logOut(true, keycloak, history);
    }

    render() {
        const { classes,
            isDrawerOpened,
            onDrawerOpen,
            hideDrawerIcon,
            model,
            location,
            keycloak,
            t } = this.props;

        const { mobileMoreAnchorEl } = this.state;

        const currentUserPerms = model ? model.permissions : [];
        const filteredMenuItems = this.menuItems.filter(item => isEnoughPermissions(item, currentUserPerms));

        const isMobileMenuOpen = Boolean(mobileMoreAnchorEl);

        const pathname = location?.pathname || '/';
        const { filters, initFilters } = this.getFiltersDataForPage(pathname);
        const { total } = calculateTotalFilters(filters, initFilters);

        const renderMobileMenu = (
            <Menu
                disableAutoFocusItem
                anchorEl={mobileMoreAnchorEl}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                open={isMobileMenuOpen}
                onClose={this.handleMenuClose}
            >
                {filteredMenuItems.map(item => (
                    <MenuItem
                        key={filteredMenuItems.indexOf(item)}
                        className={classes.mobileMenuItem}
                        component={AdapterNavLink}
                        to={item.routeTo}
                        onClick={this.handleMobileMenuClose}
                    >
                        {t(item.translateId)}
                    </MenuItem>
                ))}
            </Menu>
        );

        return (
            <Toolbar
                className={classes.root}
            >

                <IconButton
                    color="inherit"
                    aria-label="Open drawer"
                    onClick={onDrawerOpen}
                    className={classNames(
                        classes.menuButton,
                        (isDrawerOpened || !keycloak.authenticated || hideDrawerIcon) && classes.hide
                    )}
                    size="large"
                >
                    {total ? (
                        <Badge
                            className={classes.badge}
                            badgeContent={total}
                            color="error"
                            overlap="rectangular"
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'right',
                            }}
                        >
                            {this.props.drawerIconType}
                        </Badge>
                    ) : this.props.drawerIconType}

                </IconButton>

                <div
                    className={classes.logoWrapper}
                >
                    <LinkToHome
                        component="button"
                        onClick={() => { this.props.history.replace('/'); }}
                    >
                        <img
                            src={logo}
                            alt="Inventory System logo"
                            width={150}
                            height={25}
                        />
                        <p className={classes.logo}>
                            Inventory System
                        </p>
                    </LinkToHome>

                </div>
                <div className={classNames(classes.menuGroup, classes.sectionDesktop)}>
                    <React.Fragment>
                        {filteredMenuItems.map(item =>
                            (
                                <Button
                                    key={filteredMenuItems.indexOf(item)}
                                    component={AdapterNavLink}
                                    className={classes.button}
                                    to={item.routeTo}
                                    color="inherit"
                                >
                                    {t(item.translateId)}
                                </Button>
                            )
                        )}
                    </React.Fragment>
                </div>
                <div className={classNames(classes.grow)} />
                {(
                    <div className={classes.sectionMobile}>
                        <IconButton
                            aria-haspopup="true"
                            onClick={this.handleMobileMenuOpen}
                            color="inherit"
                            aria-label="Header toolbar"
                            size="large"
                        >
                            <MoreIcon />
                        </IconButton>
                    </div>
                )}

                {renderMobileMenu}
                <LanguageSwitcher />
                {keycloak.authenticated && (
                    <Button
                        color="inherit"
                        onClick={this.handleLogout}
                    >
                        {t('logout')}
                    </Button>
                )}
            </Toolbar>
        );
    }
}

Header.propTypes = {
    classes: PropTypes.object.isRequired,
    isDrawerOpened: PropTypes.bool,
    onDrawerOpen: PropTypes.func,
    onDrawerClose: PropTypes.func,
    hideDrawerIcon: PropTypes.bool,
    model: PropTypes.shape({
        permissions: PropTypes.arrayOf(PropTypes.string),
    }),
    drawerIconType: PropTypes.object.isRequired,
};

const mapStateToProps = ({ user, devicesFilters, mobileDevicesFilters, licensesFilters, VMFilters }) =>
    ({
        model: user.model,
        devicesFilters: devicesFilters.appliedFilters,
        mobileDevicesFilters: mobileDevicesFilters.appliedFilters,
        licensesFilters: licensesFilters.appliedFilters,
        VMFilters: VMFilters.appliedFilters
    });

export default compose(
    withTranslation(),
    connect(mapStateToProps,
        { logOut }),
    withStyles(styles),
    withKeycloak)(Header);
